import { useCallback, useEffect, useState } from "react";

import { debounce } from "lodash";
import { Grid, IconButton } from "@material-ui/core";
import * as XLSX from "xlsx";

import { useTranslation } from "react-i18next";
import { getLTLShipments } from "api/shipment";

import { StatusLabel } from "components/Custom";
import { InternalLink } from "components/Custom/Links";
import PickStatus from "components/Custom/PickStatus";
import {
  Button,
  DatePickerGroup,
  Dropdown,
  InputContainer,
} from "components/Form";
import { FormattedTextField } from "components/FormElements";
import PageTitle from "components/PageTitle";
import DefaultTable from "components/Table";
import { popupNotification, useLayoutDispatch } from "context/LayoutContext";
import { useUserState } from "context/UserContext";
import DCImportButton from "./DCImportButton";

import {
  Class,
  Download2,
  ExportIcon,
  FolderSpecial,
  ImportIcon,
  QrCode2,
  SnippetFolder,
  Trash,
} from "icons";

import { initialAPIListValue } from "utils/constants";
import { convertDataToTableViewObt } from "utils/convert";
import { convertDate, getPreviousDate, timezoneFormat } from "utils/date";
import { exportToExcel } from "utils/exportToExcel";
import getClearAdornment from "utils/form";
import parseJwt from "utils/jwt";
import { getWithExpiry } from "utils/storage";
import { toTitleCase } from "utils/string";

import ShipmentDrawer from "./ShipmentDrawer";
import { downloadShipmentTemplate } from "./utils/shipment";
import ShipmentImport from "pages/shipments/ShipmentImport";
import { DeleteShipmentDialog } from "pages/shipments/ShipmentActions";

function LTLDeleteAction({ shipment, reload }) {
  const { permissions } = useUserState();
  const [open, setOpen] = useState(false);
  const canDelete =
    permissions?.includes("contenttypes.shipment.cancel") &&
    ["Open", "Picking"].includes(shipment.status);

  if (!canDelete) {
    return null;
  }

  return (
    <>
      <IconButton size="small" onClick={() => setOpen(true)} title="Delete">
        <Trash color="red" size={0.7} />
      </IconButton>
      {open && (
        <DeleteShipmentDialog
          shipment_number={shipment.shipment_number}
          close={() => setOpen(false)}
          afterDeleted={reload}
        />
      )}
    </>
  );
}

export default function SellerLTLShipments() {
  const layoutDispatch = useLayoutDispatch();
  const [t] = useTranslation();
  const claims = parseJwt(getWithExpiry("access"));
  const { seller_id } = claims;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [search, setSearch] = useState({
    is_small_carrier: false, // default
    with_details: true,
    status: [],
    shipment_number: "",
    sku: "",
    po_number_with_details: "",
    order__customer_id: "",
    product_type: 1,
    seller_id: seller_id,
    start_date: convertDate(getPreviousDate(new Date(), 0, 0, 30)),
    end_date: convertDate(new Date()),
  });
  const [orderDirection, setOrderDirection] = useState("desc");
  const [orderBy, setOrderBy] = useState("created_date");
  const [shipments, setShipments] = useState(initialAPIListValue);
  const [exportDisabled, setExportDisabled] = useState(false);
  const [headerCounts, setHeaderCounts] = useState({});
  const [shipment, setShipment] = useState(null);
  const [shipmentsImportData, setShipmentsImportData] = useState(
    initialAPIListValue,
  );

  const handleFileChange = (e) => {
    const files = e.target.files;
    console.log("files", files);
    console.log("file", e.target.file);

    const f = files[0];
    const reader = new FileReader();

    reader.onload = function (e) {
      const data = e.target.result;
      const readedData = XLSX.read(data, {
        type: "binary",
        cellDates: true,
        dateNF: "yyyy-mm-dd",
      });
      const wsname = readedData.SheetNames[0];
      const ws = readedData.Sheets[wsname];

      const values = XLSX.utils
        .sheet_to_json(ws, { raw: false })
        .filter((v) => v["PO Number"] && v["Customer Name"] && v["SKU"]);

      if (!values.length) {
        popupNotification({
          dispatch: layoutDispatch,
          message:
            "Please add PO Number, Customer Name, SKU columns in your excel file",
          status: "error",
        });
        return;
      }

      if (values.length > 500) {
        popupNotification({
          dispatch: layoutDispatch,
          message: "Shipment count cannot be more than 500.",
          status: "error",
        });
        return;
      }

      // generate shipment and order #
      values.forEach((value, index) => {
        const first_char = value["Marketplace"][0].toLowerCase();
        const sn =
          "obt-" +
          first_char +
          value["Marketplace"]
            .slice(1)
            .toLowerCase()
            .replace(/[aeiou&%/?+#\s]/g, "")
            .substring(0, 2) +
          "-" +
          value["PO Number"];
        value["Shipment Number"] = sn;
        value["Order Number"] = sn;
        value["Line Number"] = index + 2;
      });

      setShipmentsImportData({
        count: values.length,
        items: values,
      });
    };
    reader.readAsBinaryString(f);
    e.target.value = "";
  };

  useEffect(() => {
    if (shipments.isFetching) {
      let params = {
        ...search,
        limit: rowsPerPage,
        offset: rowsPerPage * page,
      };
      if (orderDirection && orderBy) {
        params.ordering = `${orderDirection === "asc" ? "" : "-"}${orderBy}`;
      }
      getLTLShipments({
        params,
        setFunc: (res) => {
          setShipments(res);
          setHeaderCounts(res.customCounts);
        },
        paginated: true,
      });
    }
  }, [
    search,
    page,
    rowsPerPage,
    shipments.isFetching,
    orderDirection,
    orderBy,
  ]);

  const onSort = (header) => {
    const isAsc = orderBy === header && orderDirection === "asc";
    setOrderDirection(isAsc ? "desc" : "asc");
    setOrderBy(header);
    setShipments({ ...shipments, isFetching: true });
  };

  const handleChangePage = (e, newPage) => {
    setPage(newPage);
    setShipments((prev) => ({ ...prev, isFetching: true }));
  };

  const handleChangeRowsPerPage = ({ target: { value } }) => {
    setRowsPerPage(parseInt(value, 10));
    debouncedSearchHandler();
  };

  const handleChangeFilterSelect = (prop) => ({ target: { value } }) => {
    setSearch((prev) => ({ ...prev, [prop]: value }));
    debouncedSearchHandler();
  };

  const handleChangeFilterInput = (prop) => ({ target: { value } }) => {
    setSearch((prev) => ({ ...prev, [prop]: value }));
    debouncedSearchHandler();
  };

  const exportData = (data) => {
    const today = new Date();
    const skuHeader = {
      id: "sku",
      exportLabel: "SKU",
      render: (r) => r.sku,
    };
    const poNumberIndex = headers.findIndex(({ id }) => id === "po_number");
    const exportHeaders = [...headers];
    exportHeaders.splice(poNumberIndex + 1, 0, skuHeader);

    exportToExcel(
      [],
      `LTL-Shipments-${today.toLocaleDateString()}`,
      true,
      convertDataToTableViewObt(data, exportHeaders),
      setExportDisabled,
    );
  };

  const handleExportData = () => {
    if (shipments.items.length === shipments.count) {
      exportData(shipments.items);
    } else {
      getLTLShipments({
        params: {
          ...search,
          limit: shipments.count,
        },
        setFunc: ({ items: { results } }) => exportData(results),
      });
    }
  };

  const clearHandler = (input) => {
    setSearch({ ...search, [input]: "" });
    debouncedSearchHandler();
  };

  const clearAdornment = (input) =>
    getClearAdornment(input, clearHandler, search);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearchHandler = useCallback(
    debounce(() => {
      setPage(0);
      setShipments({ ...shipments, isFetching: true });
    }, 500),
    [],
  );

  const reloadShipments = () =>
    setShipments((prev) => ({ ...prev, isFetching: true }));

  const headers = [
    {
      id: "shipment_number",
      render: (r, exported) =>
        exported ? (
          r.shipment_number
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "normal",
              alignItems: "center",
            }}
          >
            <InternalLink
              to={`/app/seller/ltl-shipments/${r.id}`}
              children={r.shipment_number}
            />
            <LTLDeleteAction shipment={r} reload={reloadShipments} />
          </div>
        ),
      style: {
        minWidth: "150px",
      },
    },
    {
      id: "customer",
      render: (r) => toTitleCase(r.customer_id),
    },
    {
      id: "po_number",
      render: (r) => toTitleCase(r.po_number),
    },
    {
      id: "qty",
      render: (r, exported) => {
        const details = r.details;
        let picked_qty = 0;
        let total_qty = 0;
        details.forEach((detail) => {
          total_qty += detail.quantity;
          const transactions = detail.transactions;
          picked_qty += transactions
            .filter((t) => t.action_type === "Picked")
            .reduce((total, transaction) => total + transaction.quantity, 0);
        });

        if (exported) {
          return `(${picked_qty}/${total_qty})`;
        }

        return <PickStatus picked_qty={picked_qty} total_qty={total_qty} />;
      },
    },
    {
      id: "ship_via",
      render: (r) => toTitleCase(r.ship_via),
      style: {
        minWidth: "120px",
      },
    },
    {
      id: "created_date",
      label: "Created",
      render: (r) => timezoneFormat(r.created_date, true),
      sortable: true,
    },
    {
      id: "status",
      render: (r, exported) =>
        exported ? (
          toTitleCase(r.status)
        ) : (
          <StatusLabel status={toTitleCase(r.status)} />
        ),
    },
  ];

  return (
    <>
      <PageTitle
        window_title="LTL Shipments"
        title={
          <>
            LTL Shipments
            {
              <IconButton onClick={downloadShipmentTemplate}>
                <Download2 color="#FFF" />
              </IconButton>
            }
          </>
        }
        multipleButtons={true}
        button={
          <>
            <DCImportButton />
            <Button
              endIcon={<ImportIcon color="white" />}
              variant="contained"
              color="primary-light"
              component="label"
            >
              Import
              <input
                id="files"
                hidden
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                type="file"
                onChange={handleFileChange}
              />
            </Button>
            {shipments?.items?.length !== 0 && (
              <Button
                endIcon={<ExportIcon color="white" />}
                variant="contained"
                size="medium"
                color="primary-light"
                onClick={() => handleExportData()}
              >
                Export
              </Button>
            )}
          </>
        }
      />
      <InputContainer>
        <Grid container>
          <DatePickerGroup
            startDateValue={search.start_date}
            startDateOnChange={handleChangeFilterSelect("start_date")}
            endDateValue={search.end_date}
            endDateOnChange={handleChangeFilterSelect("end_date")}
            itemGridProps={{ xs: 12, sm: 6, md: 4, lg: 2, xl: 2 }}
          />
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormattedTextField
              id="shipment_number"
              label={t("shipment_number")}
              variant="outlined"
              fullWidth
              value={search.shipment_number}
              onChange={handleChangeFilterInput("shipment_number")}
              InputProps={clearAdornment("shipment_number")}
              LeftIcon={SnippetFolder}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormattedTextField
              id="sku"
              label={t("SKU")}
              variant="outlined"
              fullWidth
              value={search.sku}
              onChange={handleChangeFilterInput("sku")}
              InputProps={clearAdornment("sku")}
              LeftIcon={QrCode2}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormattedTextField
              id="customer"
              label={t("customer")}
              variant="outlined"
              fullWidth
              value={search.order__customer_id}
              onChange={handleChangeFilterInput("order__customer_id")}
              InputProps={clearAdornment("order__customer_id")}
              LeftIcon={FolderSpecial}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormattedTextField
              id="po_number"
              label="PO #"
              variant="outlined"
              fullWidth
              value={search.po_number_with_details}
              onChange={handleChangeFilterInput("po_number_with_details")}
              InputProps={clearAdornment("po_number_with_details")}
              LeftIcon={FolderSpecial}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Dropdown
              id="status"
              title={t("status")}
              value={search.status}
              onChange={handleChangeFilterSelect("status")}
              items={["Open", "Picked", "Ready", "Shipped", "Invoiced"].map(
                (item) => ({
                  value: item,
                  label: item,
                }),
              )}
              all_option_active={false}
              LeftIcon={Class}
              multiple
              checkmark={true}
            />
          </Grid>
        </Grid>
      </InputContainer>
      <Grid container spacing={4}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          {shipmentsImportData?.count ? (
            <ShipmentImport
              open={true}
              shipments={shipmentsImportData}
              close={(status) => {
                setShipmentsImportData(initialAPIListValue);
                if (status) {
                  setShipments((prev) => ({ ...prev, isFetching: true }));
                }
              }}
              is_ltl={true}
            />
          ) : null}

          <DefaultTable
            data={shipments}
            headers={headers}
            headerCounts={headerCounts}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            disableExportData={true}
            handleExportData={handleExportData}
            sortActions={{ onSort, orderBy, orderDirection }}
            exportDisabled={exportDisabled}
            setExportDisabled={setExportDisabled}
            filters={search}
            rowOnClick={(shipment) => setShipment(shipment)}
          />
          {shipment && (
            <ShipmentDrawer
              open={true}
              shipmentId={shipment.id}
              onClose={(status) => {
                if (status) {
                  setShipments({ ...shipments, isFetching: true });
                }
                setShipment(null);
              }}
              onFileDelete={() => {
                setShipments({ ...shipments, isFetching: true });
                setShipment(null);
              }}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
}
